import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Search,
  AlertCircle,
  ArrowUpRight,
  Settings
} from "lucide-react";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Card } from "../../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../components/ui/table";
import { TabsContent } from "../../../components/ui/tabs";

const UsersTab = ({
  users,
  allSystemRoles,
  error,
  setSelectedUser,
  setIsModalOpen,
  handleUpdateStatus,
  handleAssignRole
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const matchesSearch =
        (user.username && user.username.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()));

      const userRoles = user.roles?.map(r => {
        const name = typeof r === 'string' ? r : r.name;
        return (name || '').toLowerCase();
      }) || [];

      const matchesRole =
        roleFilter === "all" ||
        (roleFilter === "gast" && userRoles.length === 0) ||
        userRoles.some(r => r.includes(roleFilter.toLowerCase()));

      const userStatus = (user.subscriptions?.[0]?.status || "inaktiv").toLowerCase();
      const matchesStatus =
        statusFilter === "all" ||
        userStatus === statusFilter;

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, searchTerm, roleFilter, statusFilter]);

  const availableRolesList = useMemo(() => {
    // Unique role names from the allSystemRoles state
    return allSystemRoles.map(r => r.name).sort();
  }, [allSystemRoles]);

  return (
    <TabsContent value="users" className="space-y-4 outline-none">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
            <Input
              placeholder="Benutzer suchen..."
              className="pl-9 h-9 text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            className="h-9 px-3 bg-background border rounded-md text-xs font-medium focus:ring-2 focus:ring-primary/20 outline-none"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="all">Alle Rollen</option>
            {availableRolesList.map(role => (
              <option key={role} value={role}>{role}</option>
            ))}
            <option value="gast">Gast</option>
          </select>

          <select
            className="h-9 px-3 bg-background border rounded-md text-xs font-medium focus:ring-2 focus:ring-primary/20 outline-none"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Alle Status</option>
            <option value="aktiv">Aktiv</option>
            <option value="inaktiv">Inaktiv</option>
            <option value="gekündigt">{t("adminDashboard.cancelled")}</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-md flex items-center space-x-2">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      <Card className="border shadow-none bg-white dark:bg-slate-800 rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Benutzer</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead className="text-center">Scans</TableHead>
              <TableHead className="text-center">Rollen</TableHead>
              <TableHead className="text-right">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <TableRow key={user._id} className="group">
                  <TableCell>
                    <div className="flex items-center space-x-3">
                      <div className="h-8 w-8 bg-muted rounded-full flex items-center justify-center text-muted-foreground uppercase font-bold text-xs">
                        {user.username.substring(0, 2)}
                      </div>
                      <div className="flex flex-col">
                        <span className="font-semibold text-sm">{user.username}</span>
                        <span className="text-xs text-muted-foreground">{user.email}</span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">
                        {user.subscriptions?.[0]?.planName || (
                          <span className="text-muted-foreground italic">Kein Abo</span>
                        )}
                      </span>
                      <span className="text-[10px] text-muted-foreground font-bold uppercase">
                        {user.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-center font-medium text-sm">
                    {user.scans?.length || 0}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-center">
                      {user.roles && user.roles.length > 0 ? (
                        <Badge
                          variant={(typeof user.roles[0] === 'string' ? user.roles[0] : user.roles[0].name)?.toLowerCase().includes('admin') ? 'default' : 'secondary'}
                          className="text-[9px] px-1.5 py-0"
                        >
                          {typeof user.roles[0] === 'string' ? user.roles[0] : user.roles[0].name}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[9px] opacity-70">Gast</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSelectedUser(user);
                          setIsModalOpen(true);
                        }}
                        className="h-8 w-8"
                      >
                        <ArrowUpRight size={16} />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUpdateStatus(user._id, user.status === 'active' ? 'inactive' : 'active')}
                        className={`h-8 px-2 text-[10px] font-bold ${
                          user.status === 'active' ? 'text-destructive border-destructive/20 hover:bg-destructive/10' : 'text-success border-success/20 hover:bg-success/10'
                        }`}
                      >
                        {user.status === 'active' ? 'DEAKTIVIEREN' : 'AKTIVIEREN'}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleAssignRole(user)}
                        className="h-8 px-3 text-[10px] font-bold"
                      >
                        ROLLEN
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="h-40 text-center text-muted-foreground italic">
                  Keine Benutzer gefunden.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </TabsContent>
  );
};

export default UsersTab;
